/**
 * MileageSettings — page where the user toggles between CRA rate and a custom
 * per-km reimbursement rate. Admins also see a "Refresh from canada.ca"
 * button that re-scrapes the official rate page and updates the global cache.
 * Also exposes a date-range Internal Expense Report (PDF download + email
 * handoff) covering all logged visits.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Loader2, RefreshCw, Save, FileText, Mail, Info,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

export default function MileageSettings() {
  const { get, put, post } = useApi();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [customRate, setCustomRate] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  // Report controls
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [emailing, setEmailing] = useState(false);

  async function loadRate() {
    setLoading(true);
    try {
      const r = await get('/mileage/rate');
      setRate(r);
      setCustomRate(r?.custom_rate_per_km != null ? String(r.custom_rate_per_km) : '');
      setUseCustom(!!r?.use_custom);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadRate(); }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        custom_rate_per_km: customRate ? parseFloat(customRate) : null,
        use_custom: useCustom,
      };
      const r = await put('/mileage/rate', payload);
      setRate(r);
      toast.success('Mileage rate updated');
    } catch (e) {
      toast.error(e?.message || 'Could not save rate');
    } finally {
      setSaving(false);
    }
  }

  async function handleRefreshCra() {
    setRefreshing(true);
    try {
      const r = await post('/mileage/rate/refresh-cra', {});
      if (r?.success) {
        toast.success(`CRA rate refreshed: $${r.rate_per_km}/km (${r.tax_year || ''})`);
      } else {
        toast.warning(r?.message || 'Could not refresh — cached value retained.');
      }
      await loadRate();
    } catch (e) {
      toast.error('Could not refresh CRA rate');
    } finally {
      setRefreshing(false);
    }
  }

  async function downloadInternalReport() {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
      const qs = new URLSearchParams();
      if (startDate) qs.set('start_date', startDate);
      if (endDate) qs.set('end_date', endDate);
      const url = `${apiBase}/api/mileage/report/internal${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Report failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `mileage-expense-${startDate || 'all'}-${endDate || 'all'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      toast.error(e?.message || 'Could not download report');
    }
  }

  async function emailInternalReport() {
    setEmailing(true);
    try {
      const r = await post('/mileage/report/email-handoff', {
        report_kind: 'internal',
        start_date: startDate || null,
        end_date: endDate || null,
      });
      toast.success('Draft created in Email Designer');
      if (r?.design_id) {
        window.open('/email-designer', '_blank');
      }
    } catch (e) {
      toast.error('Could not prepare email draft');
    } finally {
      setEmailing(false);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const craSourceLabel = {
    live: 'Live (canada.ca)',
    cached: 'Cached',
    default: 'Default (no fetch yet)',
  }[rate?.cra_source] || 'Unknown';

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
        <div>
          <h1 className="font-serif text-3xl">Mileage Rate</h1>
          <p className="text-muted-foreground mt-1">
            Per-kilometre rate used for visit logging and expense reports.
          </p>
        </div>

        {/* CRA rate card */}
        <Card className="rounded-sm" data-testid="cra-rate-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base font-medium">CRA Rate</CardTitle>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshCra}
                  disabled={refreshing}
                  data-testid="refresh-cra-btn"
                >
                  {refreshing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Refresh from canada.ca
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-semibold" data-testid="cra-rate-value">
                CRA Rate: ${rate?.cra_rate_per_km?.toFixed?.(4)}/km
              </span>
              {rate?.cra_tax_year && (
                <Badge variant="outline" className="rounded-sm">
                  Tax year {rate.cra_tax_year}
                </Badge>
              )}
              <Badge variant="outline" className="rounded-sm text-xs">
                {craSourceLabel}
              </Badge>
            </div>
            {rate?.cra_last_fetched_at && (
              <p className="text-xs text-muted-foreground mt-2">
                Last fetched {new Date(rate.cra_last_fetched_at).toLocaleString()}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Reasonable per-kilometre allowance (base provinces, first 5,000 km/year).
              CRA updates this rate once a year; admins can refresh on demand.
            </p>
          </CardContent>
        </Card>

        {/* Custom rate card */}
        <Card className="rounded-sm" data-testid="custom-rate-card">
          <CardHeader>
            <CardTitle className="text-base font-medium">Custom Rate</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="use-custom-toggle" className="font-medium">
                  Use my custom rate
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When on, the rate below overrides the CRA rate for all
                  calculations and new reports. Already-saved visits keep the
                  rate that was active when they were logged.
                </p>
              </div>
              <Switch
                id="use-custom-toggle"
                checked={useCustom}
                onCheckedChange={setUseCustom}
                data-testid="use-custom-toggle"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="custom-rate">Rate per kilometre (CAD)</Label>
              <Input
                id="custom-rate"
                data-testid="custom-rate-input"
                type="number"
                step="0.0001"
                min="0"
                placeholder="e.g. 0.65"
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Enter your employer&apos;s per-km reimbursement rate if it differs from CRA.
              </p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-sm">
                Active rate now:&nbsp;
                <span className="font-semibold" data-testid="active-rate-display">
                  ${rate?.active_rate_per_km?.toFixed?.(4)}/km
                </span>{' '}
                <Badge variant="outline" className="rounded-sm ml-1 text-xs">
                  {rate?.active_rate_type === 'custom' ? 'Custom' : 'CRA'}
                </Badge>
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="save-rate-btn">
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Internal report card */}
        <Card className="rounded-sm" data-testid="internal-report-card">
          <CardHeader>
            <CardTitle className="text-base font-medium">Expense Report</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Internal expense submission with full per-visit detail (rate,
              CRA/custom flag, dollar amount, running total).
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="report-start">From</Label>
                <Input
                  id="report-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="report-start-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="report-end">To</Label>
                <Input
                  id="report-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="report-end-date"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={downloadInternalReport}
                data-testid="download-internal-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={emailInternalReport}
                disabled={emailing}
                data-testid="email-internal-btn"
              >
                {emailing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send via Email Designer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
